import { getFieldValuesByTableAndId } from "./api/keyvalue";
import { toast } from "react-toastify";

export const extractHashtagsFromUrl = (pathname) => {
  const hashtagRegex = /#(\w+)/g;
  const matches = pathname.match(hashtagRegex);
  return matches ? matches.map((match) => match.substring(1)) : [];
};

export function getInitials(str) {
  return str
    .trim()
    .split(/\s+/)
    .map((word) => word[0].toUpperCase())
    .join("");
}

export function debounce(fn, wait = 400) {
  let t = null;
  const d = (...args) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
  d.cancel = () => {
    if (t) clearTimeout(t);
  };
  return d;
}

export const dateLocalComparisons = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const measureTextWidth = (
  text,
  font = '16px "Rethink Sans", "Open-sans", Arial, sans-serif',
) => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
};

export function isNumber(value) {
  if (value === null || value === undefined) {
    return false;
  }
  return typeof value === "number" && !isNaN(value);
}

export const getAdjustedHeight = (subtractPixels, currentPercentage = 100) => {
  const vh = (window.innerHeight / 100) * currentPercentage;
  const heightPx = vh - subtractPixels;
  return `${heightPx}px`;
};

export const errorMessageToast = (
  error,
  autoClose = 5000,
  containerId = "Main",
) => {
  if (error?.response?.data?.message) {
    toast.error(error?.response?.data?.message, {
      autoClose,
      containerId,
    });
  } else {
    toast.error("Error occurred", {
      autoClose,
      containerId,
    });
  }
};

export function toFloatOrNull(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function base64ToBlob(base64, mimeType) {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

export function base64ToFile(base64, filename, mimeType) {
  const blob = base64ToBlob(base64, mimeType);
  return new File([blob], filename, { type: mimeType });
}

export const addToLocalStrage = (data) => {
  Object.entries(data).forEach(([key, value]) => {
    localStorage.setItem(key, value);
  });
};

export const hasDuplicateSubscriptions = (wsConnection, topic) => {
  const currentSubscriptions = wsConnection.getSubscriptions();
  const currentSubscription = currentSubscriptions.find(
    (sub) => sub.topic === topic,
  );
  return !!currentSubscription;
};

export function deepEqual(a, b) {
  if (a === b) return true;

  if (a instanceof Date && b instanceof Date)
    return a.getTime() === b.getTime();

  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  )
    return false;

  if (Object.keys(a).length !== Object.keys(b).length) return false;

  for (let key in a) {
    if (!(key in b)) return false;

    if (!deepEqual(a[key], b[key])) return false;
  }

  return true;
}

export const deleteFromLocalStorage = (keys) => {
  keys.forEach((key) => {
    localStorage.removeItem(key);
  });
};

export function getRandomIntInclusive(min, max) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1) + min); // Now both the maximum and minimum are inclusive
}

export const getDirection = (degrees) => {
  const directions = [
    "North",
    "North East",
    "East",
    "South East",
    "South",
    "South West",
    "West",
    "North West",
  ];
  const index = Math.floor((degrees + 22.5) / 45) % 8;
  return directions[index];
};

export const getContactLabels = async (stateObject, secondary = true) => {
  let conCountryValue = "";
  let conStateValue = "";
  let conDistrictValue = "";
  let conCityValue = "";

  if (stateObject.conCountry) {
    const conCountryResponse = await getFieldValuesByTableAndId({
      tableName: "masterdata.tms_m_reg_country",
      primaryKey: "cou_id",
      fieldNames: "cou_country",
      ids: stateObject.conCountry.toString(),
    });
    conCountryValue =
      conCountryResponse && conCountryResponse.length > 0
        ? conCountryResponse[0].field1
        : "";
  }

  if (stateObject.conState) {
    const conStateResponse = await getFieldValuesByTableAndId({
      tableName: "masterdata.tms_m_reg_state",
      primaryKey: "stt_id",
      fieldNames: "stt_state_name",
      ids: stateObject.conState.toString(),
    });

    conStateValue =
      conStateResponse && conStateResponse.length > 0
        ? conStateResponse[0].field1
        : "";
  }

  if (stateObject.conDistrict) {
    const conDistrictResponse = await getFieldValuesByTableAndId({
      tableName: "masterdata.tms_m_reg_district",
      primaryKey: "dis_id",
      fieldNames: "dis_district",
      ids: stateObject.conDistrict.toString(),
    });

    conDistrictValue =
      conDistrictResponse && conDistrictResponse.length > 0
        ? conDistrictResponse[0].field1
        : "";
  }

  if (stateObject.conCity) {
    const conCityResponse = await getFieldValuesByTableAndId({
      tableName: "masterdata.tms_m_reg_cities",
      primaryKey: "cit_id",
      fieldNames: "cit_city",
      ids: stateObject.conCity.toString(),
    });

    conCityValue =
      conCityResponse && conCityResponse.length > 0
        ? conCityResponse[0].field1
        : "";
  }

  let conSecCountryValue = "";
  let conSecStateValue = "";
  let conSecDistrictValue = "";
  let conSecCityValue = "";

  if (secondary) {
    if (stateObject.conSecCountry) {
      const conSecCountryResponse = await getFieldValuesByTableAndId({
        tableName: "masterdata.tms_m_reg_country",
        primaryKey: "cou_id",
        fieldNames: "cou_country",
        ids: stateObject.conSecCountry.toString(),
      });
      conSecCountryValue =
        conSecCountryResponse && conSecCountryResponse.length > 0
          ? conSecCountryResponse[0].field1
          : "";
    }

    if (stateObject.conSecState) {
      const conSecStateResponse = await getFieldValuesByTableAndId({
        tableName: "masterdata.tms_m_reg_state",
        primaryKey: "stt_id",
        fieldNames: "stt_state_name",
        ids: stateObject.conSecState.toString(),
      });
      conSecStateValue =
        conSecStateResponse && conSecStateResponse.length > 0
          ? conSecStateResponse[0].field1
          : "";
    }

    if (stateObject.conSecDistrict) {
      const conSecDistrictResponse = await getFieldValuesByTableAndId({
        tableName: "masterdata.tms_m_reg_district",
        primaryKey: "dis_id",
        fieldNames: "dis_district",
        ids: stateObject.conSecDistrict.toString(),
      });
      conSecDistrictValue =
        conSecDistrictResponse && conSecDistrictResponse.length > 0
          ? conSecDistrictResponse[0].field1
          : "";
    }

    if (stateObject.conSecCity) {
      const conSecCityResponse = await getFieldValuesByTableAndId({
        tableName: "masterdata.tms_m_reg_cities",
        primaryKey: "cit_id",
        fieldNames: "cit_city",
        ids: stateObject.conSecCity.toString(),
      });
      conSecCityValue =
        conSecCityResponse && conSecCityResponse.length > 0
          ? conSecCityResponse[0].field1
          : "";
    }
  }

  return {
    conCountryValue,
    conStateValue,
    conDistrictValue,
    conCityValue,
    conSecCountryValue,
    conSecStateValue,
    conSecDistrictValue,
    conSecCityValue,
  };
};

export const getNullSafeLabel = (obj, prop, nullPlaceholder = "N/A") => {
  return obj ? (obj[prop] ? obj[prop] : nullPlaceholder) : nullPlaceholder;
};

export const calculatePercentage = (length, threshold) => {
  return (threshold / length) * 100;
};

export const convertToBlocks = (content, blockCount) => {
  const noOfItems = content.length;
  const quotient = Math.floor(noOfItems / blockCount);
  let remainder = noOfItems % blockCount;
  let sliceStart = 0;
  const blockList = [];
  for (let i = 1; i <= blockCount; i++) {
    let itemsPerCol;
    if (remainder > 0) {
      itemsPerCol = quotient + 1;
      remainder--;
    } else {
      itemsPerCol = quotient;
    }
    const column = content.slice(sliceStart, sliceStart + itemsPerCol);
    blockList.push(column);
    sliceStart = sliceStart + itemsPerCol;
  }
  return blockList;
};

export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result); // includes data:image/png;base64,...
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
